import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { faChevronLeft, faListCheck, faPlusCircle, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faTiktok } from '@fortawesome/free-brands-svg-icons';
import { ModalService } from '../../../../core/services/modal/modal.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { PlatformService } from '../../../../core/services/platform/platform.service';
import { ProductSetApiService } from '../../../product-set/services/api/product-set-api.service';
import { WebConfigService } from '../../../../core/services/web-config/web-config.service';

interface ProductForm {
  sku: string;
  name: string;
  qty: number;
  srp_price: number;
  price: number;
  cost: number;
  cost_added: number;
}
@Component({
  selector: 'app-create-tiktok-set',
  templateUrl: './create-tiktok-set.component.html',
  styleUrl: './create-tiktok-set.component.css'
})
export class CreateTiktokSetComponent {
  faPlusCircle = faPlusCircle;
  faXmark = faXmark;
  faChevronLeft = faChevronLeft
  faListCheck = faListCheck;
  faTiktok = faTiktok;

  get costPriceIndex() { return this.webConfigService.tiktokCostPriceIndex; }
  get srpPriceIndex() { return this.webConfigService.srpPriceIndex; }
  get percent() { return this.webConfigService.percenTikTokSetList; }

  form: FormGroup;

  keyword = '';
  productsFilter: any[] = [];
  showProductFilter = false;

  channels: any[] = [];
  channelSelected: any[] = [];

  constructor(
    private fb: FormBuilder,
    private productSetApiService: ProductSetApiService,
    private modalService: ModalService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private platformService: PlatformService,
    private webConfigService: WebConfigService
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      products: this.fb.array([], [Validators.required]),
      note: ['']
    });
    this.channels = this.platformService?.channelsAll.filter(item => item.platform === 'TIKTOK') || []
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const action = params['action'];
      const data = params['data'];
      if (action === 'copy') {
        this.setDefaultCopyProduct(data)
      }
    });
  }

  async setDefaultCopyProduct(setSku: string) {
    const productSetResponse: any = await this.productSetApiService.getAllTiktokSet({ set_sku: setSku })
    if (!productSetResponse.success && productSetResponse?.data?.length <= 0) {
      return
    }

    const productSetDetials = productSetResponse?.data[0]
    this.form.get('name')?.setValue(`${productSetDetials?.name} (COPY)`)
    const productDetials = productSetDetials?.product_info || []
    const products = productSetDetials?.products || []
    for (const product of productDetials) {
      if (product.prices) {
        const productMatch = products.find((item: any) => item.sku === product.sku);
        this.selectProduct(product, productMatch.qty, productMatch.srp_price)
      } else {
        this.modalService.openModal('fail', 'ไม่พบข้อมูลสินค้าบางรายการ', 'ติดเจ้าหน้าที่เพื่ออัพเดตข้อมูลสินค้าก่อน')
      }
    }
  }

  // Get products form array
  get products(): FormArray {
    return this.form.get('products') as FormArray;
  }

  // Search for products
  async productSearch() {
    if (this.keyword.length > 2) {
      const uniqueSku = this.products.controls.map(e => e.value.sku);
      this.productsFilter = (await this.searchProduct(this.keyword)).filter((e: any) => !uniqueSku.includes(e.sku));
      this.showProductFilter = true;
    } else {
      this.showProductFilter = false;
    }
  }

  clearSearch() {
    this.keyword = '';
    this.showProductFilter = false;
    setTimeout(() => {
      document.getElementById('product')?.focus();
    }, 50);
  }

  // Search product through API
  async searchProduct(keyword: string) {
    return await this.productSetApiService.searchProduct({ keyword, limit: 20 }).then((res: any) => {
      return res.success ? res.data : [];
    });
  }

  // Add a selected product to the form
  selectProduct(product: any, defaultQty: number, defaultSrpPrice: number) {
    if (!product.prices) {
      this.modalService.openModal('fail', 'ไม่พบราคาสินค้า', 'กรุณาอัปเดตราคาสินค้าก่อน');
      return;
    }

    const cost = product.prices[this.costPriceIndex];
    const costAdded = this.addPercentage(cost, this.percent);

    // Add the new product to the form array
    this.products.push(this.fb.group({
      sku: [product.sku],
      name: [product.name],
      qty: [defaultQty, [Validators.required, Validators.min(1)]],
      srp_price: [null, [Validators.required, Validators.min(0)]],
      price: [null, [Validators.required, Validators.min(0)]],
      cost: [cost],
      cost_added: [costAdded]
    }));

    // Clear product search list
    this.productsFilter = this.productsFilter.filter((e: any) => e.sku !== product.sku);
    if (this.productsFilter.length === 0) {
      this.showProductFilter = false;
    }
  }

  // Remove a product from the form array
  removeProduct(index: number) {
    this.products.removeAt(index);
  }

  // Add percentage to cost
  addPercentage(value: number, percent: number): number {
    return value + (value * (percent / 100));
  }

  // Calculate total quantity
  summaryItem() {
    return parseFloat(this.products.controls.reduce((sum, control) => sum + control.value.qty, 0).toFixed(2));
  }

  // Calculate total SRP
  summarySRP() {
    return parseFloat(this.products.controls.reduce((sum, control) => sum + (control.value.srp_price * control.value.qty), 0).toFixed(2));
  }

  // Calculate total cost added
  summaryCostAdded() {
    return parseFloat(this.products.controls.reduce((sum, control) => sum + (control.value.cost_added * control.value.qty), 0).toFixed(2));
  }

  // Calculate total selling price
  summaryPrice() {
    return parseFloat(this.products.controls.reduce((sum, control) => sum + control.value.price, 0).toFixed(2));
  }

  checkListOnChange(event: any) {
    const checked = event.target.checked;
    const value = event.target.value;
    const name = this.channels.find(e => e.shop_ref === value)?.platform_name;
    if (checked) {
      this.channelSelected.push({
        channel: value,
        name,
        checked: false
      })
    } else {
      this.channelSelected = this.channelSelected.filter(e => e.channel !== value)
    }
  }

  // Submit the form
  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    // Handle form submission
    const formValue: any = this.form.value
    const body = {
      "name": formValue.name,
      "products": formValue.products.map((item: any) => ({
        "sku": item.sku,
        "qty": item.qty,
        "price": item.price,
        "cost": item.cost,
        "srp_price": item.srp_price
      })),
      total_price: parseFloat(formValue.products.reduce((sum: number, item: any) => sum + item.price, 0).toFixed(2)),
      note: formValue.note,
      check_list: this.channelSelected
    }
    this.productSetApiService.createTiktokSet(body).then((response: any) => {
      if (response.success) {
        this.modalService.openModal('success', 'สำเร็จ', 'สร้างเซ็ตสินค้าเรียบร้อยแล้ว');
        this.router.navigate(['/tiktok-set/list'])
      } else {
        this.modalService.openModal('fail', 'ไม่สำเร็จ', response.message);
      }
    })
  }

  // Reset the form
  cancel() {
    this.form.reset();
    while (this.products.length) {
      this.products.removeAt(0);
    }
    this.clearSearch();
    this.location.back();
  }

}
